// frontend/src/lib/api-envelope.ts
//
// One place that knows how the backend wraps a response body, because there
// are three shapes and the payload sits under a different key in each.
//
//   { success: true,  data:  <body> }   statusAwareApiResponseWrapper, 2xx
//   { success: false, error: <body> }   statusAwareApiResponseWrapper, 4xx/5xx
//   <body>                              a route that opted out, or whose body
//                                       already carried `success` and was
//                                       therefore passed through unwrapped
//
// The third case is not hypothetical: the status-aware wrapper skips wrapping
// any body that already contains `success`, and ~89 backend call sites emit
// one. A reader that treats `success: true` as proof of an envelope returns
// `undefined` for every one of them.
//
// Getting this wrong is silent. The caller reads its fields off the envelope
// instead of the payload, finds them undefined, and behaves as though the
// server said nothing — which is how a lock-loss 409 stopped putting the
// editor into read-only, leaving a user typing into a model they no longer
// held the lock on.

/**
 * Returns the route's own body, whichever envelope it arrived in.
 *
 * Non-objects, arrays and `null` are returned untouched: they are never
 * envelopes, and throwing on them would turn a degraded response into a crash.
 */
export function envelopePayload(body: unknown): unknown {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return body

  const outer = body as Record<string, unknown>
  if (!('success' in outer)) return body

  // The discriminator alone is not enough — the matching payload key has to be
  // present too, or this is a route body that merely happens to carry
  // `success` and must be returned as-is.
  if (outer['success'] === true && 'data' in outer) return outer['data']
  if (outer['success'] === false && 'error' in outer) return outer['error']

  return body
}

/**
 * A human-readable reason out of an error body, guaranteed to be a string.
 *
 * Separate from `envelopePayload` because the payload of an error is an OBJECT
 * — `{error: '…', code: '…'}` — and callers that want a message keep reaching
 * for `.error` on the envelope instead, which hands them that object. Dropped
 * into a `string[]` it becomes "[object Object]" in a toast, or React's
 * "Objects are not valid as a React child" when a messages panel renders it.
 *
 * The guarantee is the point: every caller here is feeding a string-typed
 * field, and TypeScript cannot catch the violation because the body is parsed
 * from JSON as `any`/`unknown`.
 */
export function envelopeErrorText(body: unknown, fallback: string): string {
  const payload = envelopePayload(body)
  if (typeof payload === 'string' && payload !== '') return payload
  if (payload === null || typeof payload !== 'object') return fallback

  // `message` first: routes use `error` for a category ("Invalid request body")
  // and `message` for the specific thing that went wrong.
  const { message, error } = payload as { message?: unknown; error?: unknown }
  for (const candidate of [message, error]) {
    if (typeof candidate === 'string' && candidate !== '') return candidate
  }
  return fallback
}
